import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { MAIN_SITE_URL } from '@/mocks/categories';
import { getArticleBySlug, articleList } from '@/mocks/articles';
import ArticleLayout from './components/ArticleLayout';
import JsonLd from '@/components/base/JsonLd';
import NotFound from '@/pages/NotFound';

const SITE_URL = 'https://vigorbuyspread.site';

export default function ArticleDetail() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return <NotFound />;
  }

  const pageUrl = `${SITE_URL}/articles/${article.slug}`;

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: article.title,
        description: article.description,
        image: article.heroImage,
        url: pageUrl,
        datePublished: '2026-07-22',
        dateModified: '2026-07-22',
        author: {
          '@type': 'Organization',
          name: 'VigorBuy Spreadsheet',
          url: SITE_URL,
        },
        publisher: {
          '@type': 'Organization',
          name: 'VigorBuy Spreadsheet',
          url: SITE_URL,
          logo: {
            '@type': 'ImageObject',
            url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/a9b8fc4c2048f16265644a3fc912425a.jpeg',
          },
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': pageUrl,
        },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: SITE_URL,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: article.tags[0]?.label || 'Guide',
            item: pageUrl,
          },
        ],
      },
    ],
  };

  return (
    <main>
      <JsonLd data={articleJsonLd} />
      <header className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-background-50/95 backdrop-blur-md border-b border-background-200/50">
        <nav className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-10">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2 cursor-pointer group">
              <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
                <i className="ri-database-2-line text-lg text-white"></i>
              </div>
              <span className="font-heading text-lg text-foreground-950 whitespace-nowrap group-hover:text-primary-600 transition-colors">
                VigorBuy Spreadsheet
              </span>
            </Link>

            <div className="hidden md:flex items-center gap-3">
              <Link to="/" className="px-3 py-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors rounded-md whitespace-nowrap cursor-pointer">
                Home
              </Link>
              <Link to="/vigorbuy-spreadsheet-guide" className="px-3 py-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors rounded-md whitespace-nowrap cursor-pointer">
                Shopping Guide
              </Link>
            </div>

            <div className="flex items-center gap-3">
              <Link to="/" className="px-4 py-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors rounded-md whitespace-nowrap cursor-pointer md:hidden">
                <i className="ri-home-4-line mr-1"></i> Home
              </Link>
              <a
                href={MAIN_SITE_URL}
                target="_blank"
                rel="noopener nofollow"
                className="px-5 py-2 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Buy It Now
              </a>
            </div>
          </div>
        </nav>
      </header>

      <ArticleLayout article={article} />

      <footer className="bg-background-950 text-white">
        <div className="max-w-[960px] mx-auto px-4 md:px-6 lg:px-10 py-10">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-2 cursor-pointer group">
              <div className="w-7 h-7 rounded-lg bg-primary-500 flex items-center justify-center">
                <i className="ri-database-2-line text-base text-white"></i>
              </div>
              <span className="font-heading text-base text-white group-hover:text-primary-300 transition-colors">
                VigorBuy Spreadsheet
              </span>
            </Link>
            <div className="flex flex-wrap items-center gap-3 justify-center">
              {articleList.slice(0, 6).map((a) => (
                <Link
                  key={a.slug}
                  to={`/articles/${a.slug}`}
                  className="text-xs text-foreground-300 hover:text-primary-300 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {a.tags[0]?.label || 'Guide'}
                </Link>
              ))}
              <Link to="/" className="text-xs text-primary-400 hover:text-primary-300 transition-colors cursor-pointer">
                Return to Homepage
              </Link>
            </div>
            <p className="text-xs text-foreground-300">
              &copy; 2026 VigorBuy Spreadsheet. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </main>
  );
}